import { motion } from "framer-motion";
import { Camera, ShieldCheck, ClockAlert } from "lucide-react";

const POINTS = [
  {
    icon: Camera,
    title: "Documented, not verbal",
    body: "Every visit closes with a written debrief and before/after imagery. You never wonder what actually happened on-site.",
    meta: "Photo report · same day",
    spec: { unit: "mm", value: "0.02", label: "Streak tolerance" },
  },
  {
    icon: ShieldCheck,
    title: "One accountable partner",
    body: "Windows, awnings, sidewalks, striping, snow, graffiti, lighting — one contract, one COI, one point of escalation.",
    meta: "$2M · Insured · Bonded",
    spec: { unit: "USD", value: "2M", label: "General liability" },
  },
  {
    icon: ClockAlert,
    title: "Storm-response built in",
    body: "Weather event within 48–72 hours of your scheduled visit? We're back — no invoice, no argument. It's already priced.",
    meta: "48–72 hr re-clean · Included",
    spec: { unit: "hr", value: "48", label: "Re-clean window" },
  },
];

/**
 * WhyBespoke — with a "blueprint precision" motif:
 * measurement tick-marks along the top/left of each card,
 * corner registration marks, and a spec callout at the bottom.
 * Feels like an aerospace engineering sheet in dark mode.
 */
export default function WhyBespoke() {
  return (
    <section className="py-32 md:py-40 relative overflow-hidden" style={{ background: "var(--bg-2)" }} data-testid="why-bespoke">
      {/* Blueprint measurement rulers along top of section */}
      <div className="absolute top-0 left-0 right-0 h-12 pointer-events-none" style={{
        borderBottom: "1px solid rgba(198,168,124,0.1)",
      }}>
        <div className="max-w-[1600px] mx-auto h-full px-6 sm:px-10 lg:px-14 relative">
          {[...Array(60)].map((_, i) => (
            <div key={i} className="absolute top-8 h-4" style={{
              left: `${(i / 60) * 100}%`,
              width: "1px",
              background: i % 5 === 0 ? "rgba(198,168,124,0.35)" : "rgba(198,168,124,0.12)",
              height: i % 5 === 0 ? "12px" : "6px",
            }} />
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-20 mt-10"
        >
          <div className="md:col-span-2 overline pt-2">
            Doc · 001-A
            <div className="mt-2 font-mono text-[9px] tracking-[0.28em]" style={{ color: "var(--text-3)" }}>
              Rev. 12 / 26
            </div>
          </div>
          <div className="md:col-span-10">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              Three reasons portfolio managers <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>stay.</span>
            </h2>
            <p className="mt-8 font-body text-lg md:text-xl max-w-3xl" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
              We are not the cheapest vendor. We are the last vendor you'll need.
            </p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {POINTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.0, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="glass-card glass-reflect float-lift p-10 md:p-12 flex flex-col min-h-[480px] relative"
                data-testid={`why-bespoke-${i}`}
              >
                {/* Corner registration marks (blueprint style) */}
                {["tl", "tr", "bl", "br"].map((corner) => {
                  const isTop = corner.startsWith("t");
                  const isLeft = corner.endsWith("l");
                  return (
                    <div key={corner}
                      className="absolute w-4 h-4 pointer-events-none"
                      style={{
                        [isTop ? "top" : "bottom"]: "14px",
                        [isLeft ? "left" : "right"]: "14px",
                        borderTop: isTop ? "1px solid var(--line-gold)" : "none",
                        borderBottom: !isTop ? "1px solid var(--line-gold)" : "none",
                        borderLeft: isLeft ? "1px solid var(--line-gold)" : "none",
                        borderRight: !isLeft ? "1px solid var(--line-gold)" : "none",
                      }}
                    />
                  );
                })}

                {/* Spec index at top-right */}
                <div className="absolute top-6 right-8 font-mono text-[9px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
                  N° {String(i + 1).padStart(3, "0")}
                </div>

                <div
                  className="w-14 h-14 flex items-center justify-center mb-8"
                  style={{
                    borderRadius: "var(--r-full)",
                    background: "linear-gradient(140deg, rgba(198,168,124,0.16), rgba(198,168,124,0.04))",
                    border: "1px solid var(--line-gold)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 0 24px rgba(198,168,124,0.1)",
                  }}
                >
                  <Icon size={20} style={{ color: "var(--gold)" }} strokeWidth={1.4} />
                </div>
                <h3 className="font-display text-3xl md:text-4xl" style={{ letterSpacing: "-0.02em", color: "var(--text)", lineHeight: 1.05 }}>
                  {p.title}
                </h3>
                <p className="mt-6 font-body text-base md:text-lg" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
                  {p.body}
                </p>

                {/* Spec callout — blueprint style */}
                <div className="mt-auto pt-8">
                  <div className="hairline mb-5" />
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--gold)" }}>
                        {p.meta}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display leading-none" style={{ fontSize: "1.8rem", color: "var(--gold-2)", letterSpacing: "-0.02em" }}>
                        {p.spec.value}
                        <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--text-3)" }}>{p.spec.unit}</span>
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.28em] mt-1" style={{ color: "var(--text-3)" }}>
                        {p.spec.label}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom precision indicator */}
        <div className="mt-14 flex items-center justify-center gap-4 font-mono text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--text-3)" }}>
          <div className="w-8 h-px" style={{ background: "var(--line-gold)" }} />
          Spec sheet · Bespoke Exterior Solutions · Certified operations
          <div className="w-8 h-px" style={{ background: "var(--line-gold)" }} />
        </div>
      </div>
    </section>
  );
}
